import { useCallback, useEffect, useRef, useState } from 'react';
import type { ScanResult } from '../engine/types';
import { runScan } from '../engine/scan';
import { publishScan, wireRerun } from '../engine/publicApi';
// Importing the index registers every probe as a side effect.
import '../probes';

export interface ScanState {
  result: ScanResult | null;
  scanning: boolean;
  error: string | null;
  start: (opts?: { gestureGranted?: boolean }) => Promise<void>;
}

/**
 * Drives a scan and exposes it to the UI.
 *
 * The scan runs once on mount (a fingerprint scanner should show its results
 * without a click), and again on demand. The optional `gestureGranted` flag is
 * for the runner's gesture-gated mechanism; no probe currently sets
 * `requiresGesture`, so callers can ignore it. The mechanism is retained (and
 * unit-tested) for probes that genuinely cannot run without a gesture.
 */
export function useScan(autoStart = true): ScanState {
  const [result, setResult] = useState<ScanResult | null>(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const running = useRef(false);

  const start = useCallback(async (opts?: { gestureGranted?: boolean }) => {
    if (running.current) return;
    running.current = true;
    setScanning(true);
    setError(null);
    try {
      wireRerun(() => runScan(opts));
      const r = await runScan(opts);
      setResult(r);
      publishScan(r);
    } catch (e) {
      setError(String((e as Error)?.message ?? e));
    } finally {
      running.current = false;
      setScanning(false);
    }
  }, []);

  useEffect(() => {
    if (autoStart) void start();
  }, [autoStart, start]);

  return { result, scanning, error, start };
}
